import { isRefusedAttemptMetadata } from "@shared/audit/refusedAttempt";
import { SkyBadge, SkyButton, SkyDataGrid, SkyText } from "@skypointcloud/skypoint-ui";
import type { SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { useLayoutEffect, useRef, type CSSProperties, type MouseEvent } from "react";
import type { AuditEventView } from "@shared/types/audit";
import type { AuditReportRow } from "@shared/types/auditReports";
import { pixelColumnLayout } from "@/lib/grid";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { auditActionLabelFor, auditEntityTypeLabel, isReadEvent } from "./actionLabels";
import { ZoneCaption } from "@/components/shell/ZoneCaption";

/**
 * The trail, and the saved reports, in one grid (canvas 09, "Event grid").
 *
 * The two row shapes are narrowed to one `AuditGridRow` here rather than given
 * two grids, because the columns, the drawer and the cursor walk are the same and
 * a second grid would drift from this one.
 *
 * **Nothing in this component sorts.** The order is the server's: break-glass
 * first for the grants report, then newest first, walked by `chain_seq` because
 * that is the only ordering that matches the hash links (SEC-12, SEC-18). Every
 * column is therefore `sortable: false` — a click that reordered the page would
 * silently break the promise the report makes to a reviewer.
 */

export interface AuditGridRow {
  id: string;
  occurredAt: string;
  actorRef: string;
  /**
   * The person's address, joined by the server when the page is read (round 4, as the tenant
   * trail's, and for a saved report's rows since #1090); null for a system actor or a person no
   * longer in `users`.
   */
  actorEmail: string | null;
  actorType: string;
  action: string;
  entityType: string;
  entityLabel: string | null;
  entityId: string | null;
  scopeType: string;
  correlationId: string;
  breakGlass: boolean;
  /** A refused attempt (`metadata.outcome: "failed"`); nothing durable changed (#962). */
  failed?: boolean;
  /** Present for a trail row; a report row carries only what the report selected. */
  event?: AuditEventView;
  /**
   * #1373: what a report row's action words read beside `failed` (its `section`), since a report
   * row has no `event`. A trail row reads `event.metadata` instead.
   */
  labelMetadata?: Record<string, unknown>;
  reason?: string | null;
}

function isBreakGlass(metadata: unknown): boolean {
  return (
    typeof metadata === "object" &&
    metadata !== null &&
    (metadata as { break_glass?: unknown }).break_glass === true
  );
}

function isFailedMetadata(metadata: unknown): boolean {
  return isRefusedAttemptMetadata(metadata);
}

/**
 * #2519: the actions whose `metadata.reason` is the reason a person typed (Grant access, Remove,
 * Reactivate, Activate and Deactivate a user, Reset sign-in, an instance's Disable, Enable, Retry
 * and Mark storage removed, Delete tenant), or one of the closed codes the drawer says in words
 * (`reasonText`). Any other action's `reason` is a code of its own (a skip, a refusal) and is not
 * a typed reason.
 */
const TYPED_REASON_ACTIONS: ReadonlySet<string> = new Set([
  "access.grant",
  "access.revoke",
  "access.reactivate",
  "user.activate",
  "user.deactivate",
  "user.rebind",
  "instance.disable",
  "instance.enable",
  "instance.provisioning_retry",
  "instance.mark_storage_removed",
  "tenant.delete",
]);

/** The reason a person typed, for the drawer's Reason line; a refused attempt's code is not one. */
function typedReasonOf(event: AuditEventView, failed: boolean): string | null {
  if (failed || !TYPED_REASON_ACTIONS.has(event.action)) return null;
  const metadata = event.metadata as { reason?: unknown } | null | undefined;
  const reason = metadata?.reason;
  return typeof reason === "string" && reason.trim() !== "" ? reason : null;
}

export function eventToRow(event: AuditEventView): AuditGridRow {
  const failed = event.failed === true || isFailedMetadata(event.metadata);
  return {
    id: event.id,
    occurredAt: event.occurredAt,
    actorRef: event.actorRef,
    actorEmail: event.actorEmail ?? null,
    actorType: event.actorType,
    action: event.action,
    entityType: event.entityType,
    entityLabel: event.entityLabel,
    entityId: event.entityId,
    scopeType: event.scopeType,
    correlationId: event.correlationId,
    breakGlass: isBreakGlass(event.metadata),
    failed,
    event,
    reason: typedReasonOf(event, failed),
  };
}

export function reportRowToRow(row: AuditReportRow): AuditGridRow {
  return {
    id: row.id,
    occurredAt: row.occurredAt,
    actorRef: row.actorRef,
    actorEmail: row.actorEmail,
    actorType: row.actorType,
    action: row.action,
    entityType: row.entityType,
    entityLabel: row.entityLabel,
    entityId: row.entityId,
    scopeType: row.scopeType,
    correlationId: row.correlationId,
    breakGlass: row.breakGlass,
    // #1373: a refused attempt reads as refused in a saved report, as it does in All events.
    failed: row.failed === true,
    ...(row.section ? { labelMetadata: { section: row.section } } : {}),
    reason: row.reason,
  };
}

/** What a row's action words read: the trail row's metadata, else the report row's (#1373). */
export function labelMetadataOf(row: AuditGridRow): unknown {
  return row.event?.metadata ?? row.labelMetadata;
}

/**
 * Fixes wave 4 (post-release check 3, 06g-platform-audit-trail.png, 1440 px): with every column a
 * percentage beside the 48 px selection column, "Change" got about 66 px, its right-aligned
 * header ran into "Reference", and "View change" wrapped. Both now have pixel widths and the
 * table a floor (`pixelColumnLayout`).
 *
 * Fixes wave 4b (post-release check 4, 05-platform-audit-1280/1440.png): "Occurred at" wrapped its
 * timestamp at both widths and a long action ran into Entity. A header's width is a fact of the
 * font, so the widths are measured in Chromium in the app's own stack ("Segoe UI", system-ui:
 * DejaVu Sans on a Linux browser, the widest) and in Inter, the larger kept. Border-box widths:
 * "Occurred at" holds "Sep 29, 2026, 09:05:44 PM" (163 px at 12 px) on one line; "Reference"
 * needs 124 px for its header; "Change" 100 px for its header and 118 px for the small button.
 */
const OCCURRED_AT_WIDTH = 192;
const REFERENCE_WIDTH = 136;
const CHANGE_WIDTH = 128;
/**
 * Each percent column's least room: its header on one line (Actor 81, Action 90, Entity 86, Scope
 * 87 px), and more for the two values an auditor reads. The floor, 968 px, fits the main pane at
 * 1280 px with the drawer expanded (about 971 px); a longer value truncates, whole in its title.
 * Round 4: the Actor is a person's address now, not a 24-character hash, and the 48 px selection
 * column is gone, so the Actor takes that room and more (152 px: "ada@northwind.example" is about
 * 140 px at 12 px in Chromium; at 1440 px its share is about 200 px).
 */
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  actorRef: 152,
  action: 136,
  entityType: 128,
  scopeType: 96,
};

/** What the grid says with no rows; the page passes its own when no filter is set (walk W19). */
export const NO_MATCHING_EVENTS = "No events match these filters.";

export function AuditEventsGrid({
  rows,
  loading,
  selectedId,
  onOpen,
  emptyMessage = NO_MATCHING_EVENTS,
}: {
  rows: AuditGridRow[];
  loading?: boolean;
  selectedId: string | null;
  onOpen: (row: AuditGridRow) => void;
  emptyMessage?: string;
}) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<AuditGridRow>[] = [
    {
      key: "occurredAt",
      header: "Occurred at",
      width: `${OCCURRED_AT_WIDTH}px`,
      sortable: false,
      renderCell: (_value, row) => {
        const stamp = formatInstant(row.occurredAt, zone, "seconds");
        // Tabular figures so columns of digits line up down the page; one line, never wrapped.
        return (
          <SkyText
            size={200}
            font="numeric"
            block
            truncate
            title={stamp}
            testId={`audit-occurred-at-${row.id}`}
          >
            {stamp}
          </SkyText>
        );
      },
    },
    {
      key: "actorRef",
      header: "Actor",
      width: "20%",
      sortable: false,
      renderCell: (_value, row) => (
        <div className="flex flex-col" data-audit-row-id={row.id}>
          {/* The person's address when the server joined one (round 4, as the tenant trail
              shows it), else the keyed hash the row stores (SEC-13). An inline SkyText ignores
              `truncate`: `block` keeps the value inside its column and the title holds the whole
              value for hover (client/AGENTS.md). */}
          <SkyText
            size={200}
            font={row.actorEmail ? "base" : "numeric"}
            block
            truncate
            title={row.actorEmail ?? row.actorRef}
            testId={`audit-actor-ref-${row.id}`}
          >
            {row.actorEmail ?? row.actorRef}
          </SkyText>
          <SkyText size={200} color="muted">
            {row.actorType}
          </SkyText>
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      width: "28%",
      sortable: false,
      renderCell: (_value, row) => (
        <div className="flex flex-wrap items-center gap-2">
          {/* Round 7 T-36: every action reads in words, here and in the Action filter
              (`AUDIT_ACTION_LABELS`). The machine name stays in the event drawer's title, the
              View button's accessible name and the CSV, which is where an auditor searches. */}
          {auditActionLabelFor(row.action, row.failed === true, labelMetadataOf(row)) ? (
            <SkyText
              size={200}
              block
              truncate
              className="max-w-full"
              title={auditActionLabelFor(row.action, row.failed === true, labelMetadataOf(row))}
              testId={`audit-action-label-${row.id}`}
            >
              {auditActionLabelFor(row.action, row.failed === true, labelMetadataOf(row))}
            </SkyText>
          ) : (
            // An action outside the closed set (a row newer than this build): its machine name
            // verbatim, cut inside the column, whole in its title.
            <SkyText
              size={200}
              font="numeric"
              block
              truncate
              className="max-w-full"
              title={row.action}
              testId={`audit-action-${row.id}`}
            >
              {row.action}
            </SkyText>
          )}
          {row.failed ? (
            <SkyBadge appearance="tint" color="warning" testId={`failed-${row.id}`}>
              Failed attempt
            </SkyBadge>
          ) : null}
          {row.breakGlass ? (
            <SkyBadge appearance="tint" color="danger" testId={`break-glass-${row.id}`}>
              Break-glass
            </SkyBadge>
          ) : null}
        </div>
      ),
    },
    {
      key: "entityType",
      header: "Entity",
      width: "32%",
      sortable: false,
      renderCell: (_value, row) => (
        <div className="flex flex-col">
          <SkyText
            size={200}
            block
            truncate
            title={auditEntityTypeLabel(row.action, row.entityType)}
            testId={`audit-entity-type-${row.id}`}
          >
            {auditEntityTypeLabel(row.action, row.entityType)}
          </SkyText>
          {row.entityLabel ? (
            <SkyText
              size={200}
              color="muted"
              block
              truncate
              title={row.entityLabel}
              testId={`audit-entity-label-${row.id}`}
            >
              {row.entityLabel}
            </SkyText>
          ) : null}
        </div>
      ),
    },
    {
      key: "scopeType",
      header: "Scope",
      width: "20%",
      sortable: false,
      // The grid's cell text style, as every other cell here (the default cell is larger).
      renderCell: (_value, row) => (
        <SkyText size={200} testId={`audit-scope-${row.id}`}>
          {row.scopeType}
        </SkyText>
      ),
    },
    {
      key: "correlationId",
      header: "Reference",
      width: `${REFERENCE_WIDTH}px`,
      sortable: false,
      renderCell: (_value, row) => (
        <SkyText
          size={200}
          font="numeric"
          block
          truncate
          title={row.correlationId}
          testId={`audit-reference-${row.id}`}
        >
          {row.correlationId}
        </SkyText>
      ),
    },
    {
      key: "open",
      // Named rather than blank: an empty column header reads as an unlabelled
      // column to a screen reader walking the table, and every cell here holds
      // a control.
      header: "Change",
      width: `${CHANGE_WIDTH}px`,
      align: "right",
      sortable: false,
      renderCell: (_value, row) => (
        <SkyButton
          appearance="subtle"
          size="small"
          testId={`view-change-${row.id}`}
          aria-label={
            isReadEvent(row.action)
              ? `View the details of ${row.action} at ${formatInstant(row.occurredAt, zone, "seconds")}`
              : `View the change ${row.action} made at ${formatInstant(row.occurredAt, zone, "seconds")}`
          }
          onClick={() => onOpen(row)}
        >
          {isReadEvent(row.action) ? "View details" : "View change"}
        </SkyButton>
      ),
    },
  ];

  // No selection column (round 4): the radios could not be used and only tinted the open row.
  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  const holder = useRef<HTMLDivElement>(null);
  const byId = new Map(rows.map((row) => [row.id, row]));

  // The open row, for a screen reader (`aria-current`) and for the tint (`.grid-current-row` in
  // `client/src/index.css`). `SkyDataGrid` takes no row attributes, so each row is found by the
  // id its Actor cell carries and marked here, after every render.
  useLayoutEffect(() => {
    const body = holder.current?.querySelector("tbody");
    if (!body) return;
    for (const tr of body.querySelectorAll<HTMLTableRowElement>(":scope > tr")) {
      const id = tr.querySelector<HTMLElement>("[data-audit-row-id]")?.dataset.auditRowId;
      if (!id) continue;
      if (id === selectedId) tr.setAttribute("aria-current", "true");
      else tr.removeAttribute("aria-current");
    }
  });

  // The whole row opens its change (round 4). A click on a control in the row is that
  // control's own ("View change" opens the same drawer itself, and is the keyboard's way in).
  const openFromRow = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input")) return;
    const tr = target.closest("tbody > tr");
    const id = tr?.querySelector<HTMLElement>("[data-audit-row-id]")?.dataset.auditRowId;
    const row = id ? byId.get(id) : undefined;
    if (row) onOpen(row);
  };

  return (
    // A mouse shortcut to the row's own "View change" button, which keyboard users reach.
    <div
      ref={holder}
      data-audit-grid-floor=""
      style={{ "--audit-grid-floor": `${layout.floor}px` } as CSSProperties}
      onClick={openFromRow}
    >
      <SkyDataGrid<AuditGridRow>
        data={rows}
        columns={layout.columns}
        loading={loading}
        getRowId={(row) => row.id}
        testId="audit-grid"
        className="grid-zebra grid-current-row [&_table]:min-w-(--audit-grid-floor) [&_tbody_tr:has([data-audit-row-id])]:cursor-pointer"
        emptyMessage={emptyMessage}
      />
      {/* B-51 (round 9): the trail's times are in the reader's zone, said once. */}
      {rows.length > 0 ? <ZoneCaption testId="audit-zone-caption" className="mt-2" /> : null}
    </div>
  );
}
